import React, { useMemo } from 'react';
import { MemberSearchQuery_members } from '@frontend/app/queries/types/MemberSearchQuery';
import { InstagramCreator, InstagramCreatorCountry } from '@frontend/gql/social/graphql';
import { ISocialAccount } from '@components';
import { useSimilarCreators } from '@frontend/components/widgets/SocialProfile/hooks/useSimilarCreators';

import { backendServerApiEndpoint } from '@frontend/applications/Shared/serviceHosts';
import { SimilarCreatorsSection } from '@/applications/Social/creator-search/instagram/components/similar-creators-section';

interface SimilarProspectsProps {
  socialData: ISocialAccount | null;
  onProspectClick: (prospect: MemberSearchQuery_members) => void;
}

// Adapter function to transform ISocialAccount to InstagramCreator (exactly like legacy)
const transformSocialAccountToInstagramCreator = (socialAccount: ISocialAccount): InstagramCreator => ({
  __typename: 'InstagramCreator',
  id: socialAccount.id.toString(),
  username: socialAccount.username || socialAccount.display_name || 'unknown',
  name: socialAccount.full_display_name || socialAccount.name || null,
  biography: socialAccount.description || null,
  profilePictureUrl: socialAccount.profile_picture || socialAccount.image || null,
  email: socialAccount.email || null,
  country: (socialAccount.country_code as InstagramCreatorCountry) || null,
  inCreatorMarketplace: false,
  insights: {
    __typename: 'InstagramCreatorInsights',
    total: {
      __typename: 'InstagramCreatorInsightsTotal',
      followers: socialAccount.follower_count || null,
    },
    recent: {
      __typename: 'InstagramCreatorInsightsRecent',
      engagement: socialAccount.recent_engagement_rate || null,
      reach: socialAccount.reach || null,
    },
  },
});

export const SimilarProspects: React.FC<SimilarProspectsProps> = ({
  socialData,
  onProspectClick,
}) => {
  // Exactly like legacy: check if social account has lookalikes
  const loadSimilarCreators = socialData?.has_lookalikes === true;

  // Exactly like legacy: use useSimilarCreators hook
  const {
    similarCreators: apiSimilarCreators,
    isFetching,
  } = useSimilarCreators({
    apiEndpoint: backendServerApiEndpoint(),
    id: loadSimilarCreators ? socialData?.id || null : null,
  });

  // Transform ISocialAccount[] to InstagramCreator[] (exactly like legacy approach)
  const similarCreators = useMemo(() => {
    if (!apiSimilarCreators) return [];

    return apiSimilarCreators.map((creator) => transformSocialAccountToInstagramCreator(creator));
  }, [apiSimilarCreators]);

  // Handler to convert InstagramCreator click back to member click
  const handleCreatorClick = (creator: InstagramCreator) => {
    // Create a member representation for the callback
    const mockMember = {
      id: parseInt(creator.id, 10) || 0,
      email: creator.email,
      name: creator.name || creator.username,
      profilePicture: creator.profilePictureUrl || null,
      createdDate: new Date().toISOString(),
    } as MemberSearchQuery_members;

    onProspectClick(mockMember);
  };

  // Exactly like legacy: only show if loadSimilarCreators is true
  return loadSimilarCreators ? (
    <SimilarCreatorsSection
      similarCreators={similarCreators}
      isFetching={isFetching}
      onCreatorClick={handleCreatorClick}
    />
  ) : (
    <div className="text-center py-8">
      <div className="text-grey-5 mb-2">Similar creators are not available for this account</div>
    </div>
  );
};
